import React from 'react';
import styled from 'styled-components';
import logoImage from '../assets/logo.png';
import { useTranslation } from 'react-i18next';

const FooterSection = styled.footer`
  background: #2c3e50;
  color: white;
  padding: 3rem 0 1rem;
  margin-top: auto;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 3rem;
  margin-bottom: 2rem;
`;

const FooterColumn = styled.div`
  h2 {
    font-size: 1.15rem;
    font-weight: 600;
    margin-bottom: 1.5rem;
    color: #ecf0f1;
  }
  
  p {
    line-height: 1.6;
    margin-bottom: 1rem;
    color: #bdc3c7;
  }
  
  a {
    color: #3498db;
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: #2980b9;
    }
  }
  
  strong {
    color: #ecf0f1;
  }
`;

const FooterLogo = styled.div`
  margin-bottom: 1.5rem;
  
  a {
    display: inline-block;
  }
  
  img {
    max-height: 32px;
    width: auto;
  }
`;

const SocialMediaSection = styled.div`
  margin-top: 1.5rem;
  
  p {
    margin-bottom: 1rem;
    font-weight: 600;
    color: #ecf0f1;
  }
`;

const SocialMediaButtons = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const SocialMediaButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  color: white;
  font-size: 1.2rem;
  text-decoration: none;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px) scale(1.1);
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  }
  
  &.instagram {
    background: linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888);
  }
  
  &.facebook {
    background: linear-gradient(45deg, #1877f2, #42a5f5);
  }
  
  &.phone {
    background: linear-gradient(45deg, #27ae60, #2ecc71);
  }
  
  img {
    width: 20px;
    height: 20px;
    filter: brightness(0) invert(1);
  }
`;

const Copyright = styled.div`
  border-top: 1px solid #34495e;
  padding-top: 2rem;
  text-align: center;
  color: #95a5a6;
  
  a {
    color: #ec5c2c;
    font-weight: 600;
    text-decoration: none;
    
    &:hover {
      color: #d44d26;
    }
  }
`;

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();
  return (
    <FooterSection id="footer">
      <Container>
        <FooterGrid className="row row-cols-1 row-cols-md-3 g-4 footer-container">
          <FooterColumn className="col-md-4">
            <FooterLogo className="footer-logo">
              <a href="/"><img src={logoImage} alt="logo" /></a>
            </FooterLogo>
            <p>{t('footer.description')}</p>
            <p>
              <a href="/privacy">{t('footer.privacy')}</a>
              <br />
              <span>{t('footer.cookies')}</span>
            </p>
            
            <SocialMediaSection>
              <p>{t('footer.social_media')}</p>
              <SocialMediaButtons>
                <SocialMediaButton 
                  className="social-media-btn instagram" 
                  target="_blank" 
                  rel="noreferrer"
                  href="https://www.instagram.com/universaldrive/"
                  title={String(t('footer.instagram'))}
                >
                  <img src="/assets/instagram-logo.svg" alt="Instagram" />
                </SocialMediaButton>
                <SocialMediaButton 
                  className="social-media-btn facebook" 
                  target="_blank" 
                  rel="noreferrer"
                  href="https://www.facebook.com/universaldrive/"
                  title={String(t('footer.facebook'))}
                >
                  <img src="/assets/facebook-logo.svg" alt="Facebook" />
                </SocialMediaButton>
                <SocialMediaButton 
                  className="social-media-btn phone" 
                  href="tel:+31616752832"
                  title={String(t('footer.phone_label'))}
                >
                  <img src="/assets/phone-logo.svg" alt="Phone" />
                </SocialMediaButton>
              </SocialMediaButtons>
            </SocialMediaSection>
          </FooterColumn>

          <FooterColumn className="col-md-4">
            <h2>{t('footer.contact_title')}</h2>
            <p>
              {t('footer.contact_description')}
            </p>
            <p>
              {t('footer.address')}
            </p>
            <p>
              {t('footer.phone')}
              <br />
              {t('footer.email')}
            </p>
            <p>
              {t('footer.instagram')}
              <br />
              {t('footer.facebook')}
              <br />
              {t('footer.phone_label')}
            </p>
          </FooterColumn>

          <FooterColumn className="col-md-4">
            <h2>{t('footer.digid_title')}</h2>
            <p>
              {t('footer.digid_description')}
            </p>
            <p>
                <strong>Link:</strong> <a target="_blank" href="https://www.cbr.nl/nl/mijncbr/rijschool-machtigen.htm">{t('footer.digid_link')}</a>
            </p>

          </FooterColumn>
        </FooterGrid>

        <Copyright className="copyright">
          © {year} Universal Drive. All rights reserved | Designed & Implemented by bigO Digital NL
        </Copyright>
      </Container>
    </FooterSection>
  );
};

export default Footer;